import { type ReactElement, useEffect, useRef, useState } from 'react';
import { shouldHoldUpdatesForFormFill } from '../lib/forms/formFillGuard';
import {
  isDismissActive,
  parseDismissRecord,
  serializeDismissRecord,
} from './dismissal';
import { recordUpdateEvent } from './updateTelemetry';
import { useOnline } from './useOnline';
import { buildLabel, isUpdateInFlight, phaseLabel } from './versionCompare';
import type { ApplyUpdateOptions, ApplyUpdatePhase, VersionInfo } from './types';

interface UpdateAvailableBannerProps {
  current: VersionInfo;
  latest: VersionInfo;
  onUpdate: (options?: ApplyUpdateOptions) => Promise<ApplyUpdatePhase>;
  /** Session-scoped dismiss key so it stays hidden until next session/escalation. */
  dismissKey: string;
  /**
   * Changes on every successful version check. A dismissal EXPIRES, and this is
   * what re-evaluates it: the banner returns on the next detected check after
   * DISMISS_RETURN_AFTER_MS, not on a timer of its own. Tying it to the check
   * means the surface can only reappear at a moment the app has just confirmed
   * a newer build really is published.
   */
  checkSignal?: number | null;
}

// Brand-compliant classes mirroring the shared <Button> primary/ghost variants.
// Hand-rolled (not imported) so this shared chrome stays portable across the CRA
// app and the three strict Vite PWAs without dragging client design components
// into their (verbatimModuleSyntax / noUnusedLocals) tsc programs.
const PRIMARY_BTN =
  'rounded-xl bg-[#007ACC] px-3 py-1.5 text-sm font-medium text-white transition-colors ' +
  'hover:bg-[#005fa3] active:bg-[#004a80] focus:outline-none focus-visible:ring-2 ' +
  'focus-visible:ring-[#007ACC]/50 disabled:opacity-50 disabled:cursor-not-allowed';
const GHOST_BTN =
  'rounded-xl px-3 py-1.5 text-sm font-medium text-slate-600 transition-colors ' +
  'hover:bg-slate-100 active:bg-slate-200 focus:outline-none focus-visible:ring-2 ' +
  'focus-visible:ring-slate-300 disabled:opacity-50';

/** Inline spinner so the loading state carries motion without imports. */
const SPINNER =
  'mr-1.5 inline-block h-3 w-3 animate-spin rounded-full border-2 ' +
  'border-white/40 border-t-white align-middle';

/**
 * Soft, dismissable "a new version is available" banner. Announced politely to
 * assistive tech (role=status). Escape dismisses (only when nothing else claims
 * the key). Distinct from the forced modal.
 *
 * Dismiss stays ENABLED while an update is in flight: on this soft path the
 * user must always be able to get the chrome out of the way and keep working —
 * an install can take minutes, and the SW takeover proceeds in the background
 * exactly as an autoUpdate registration's would either way.
 */
export function UpdateAvailableBanner({
  current,
  latest,
  onUpdate,
  dismissKey,
  checkSignal,
}: UpdateAvailableBannerProps): ReactElement | null {
  const [dismissed, setDismissed] = useState(false);
  const [phase, setPhase] = useState<ApplyUpdatePhase | null>(null);
  const online = useOnline();
  const applying = isUpdateInFlight(phase);

  // Re-show if the target build changed since the last dismiss this session,
  // OR if the dismissal has simply aged out (see dismissal.ts).
  const storageKey = `officehub.appVersion.dismissed.${dismissKey}`;
  useEffect(() => {
    let stored: string | null = null;
    try {
      stored = window.sessionStorage.getItem(storageKey);
    } catch {
      setDismissed(false);
      return;
    }
    const record = parseDismissRecord(stored);
    if (isDismissActive(record, latest.sha, Date.now())) {
      setDismissed(true);
      return;
    }
    // #4741: an EXPIRY may never raise chrome over an open form fill. While a
    // fill holds updates, a dismissal of THIS build stays in force even though
    // it has aged out — the expiry is deferred, not cancelled, and lands on the
    // first check after the hold releases. The hold can therefore only ever
    // keep the banner hidden for longer; it can never make one appear.
    if (record?.sha === latest.sha && shouldHoldUpdatesForFormFill()) {
      setDismissed(true);
      return;
    }
    setDismissed(false);
  }, [storageKey, latest.sha, checkSignal]);

  // "Was this user ever told?" — see updateTelemetry.ts for why a breadcrumb
  // and what it cannot answer.
  //
  // Keyed on the target SHA, not on the `latest` object, mirroring the
  // update-detected effect in useAppVersionCheck.ts. The poll hands down a
  // freshly-parsed object every ~5 minutes even when the published build has
  // not moved, so an object-keyed effect re-fired for as long as the banner
  // stayed on screen — turning "occasions the user was told" into "minutes the
  // banner was visible", which is not a question anyone asked.
  //
  // The ref is CLEARED on dismiss so the banner's RETURN (after the dismissal
  // ages out, see dismissal.ts) counts as the new occasion it genuinely is,
  // even though the sha has not changed. A newer sha likewise re-fires.
  const shownShaRef = useRef<string | null>(null);
  useEffect(() => {
    if (dismissed) {
      shownShaRef.current = null;
      return;
    }
    if (shownShaRef.current === latest.sha) return;
    shownShaRef.current = latest.sha;
    recordUpdateEvent('banner-shown', current, latest);
  }, [dismissed, current, latest]);

  const dismiss = () => {
    setDismissed(true);
    recordUpdateEvent('banner-dismissed', current, latest);
    try {
      window.sessionStorage.setItem(
        storageKey,
        serializeDismissRecord(latest.sha, Date.now()),
      );
    } catch {
      /* private mode / storage disabled — dismiss for this render only */
    }
  };

  useEffect(() => {
    if (dismissed) return;
    const onKey = (e: KeyboardEvent) => {
      // Only handle Escape if nothing else already consumed it (e.g. a dialog),
      // so dismissing another overlay doesn't also silently drop this banner.
      if (e.key === 'Escape' && !e.defaultPrevented) dismiss();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dismissed]);

  if (dismissed) return null;

  const handleUpdate = () => {
    if (applying) return;
    recordUpdateEvent('reload-accepted', current, latest);
    void onUpdate({ onPhase: setPhase });
  };

  const buttonText = applying
    ? phaseLabel(phase)
    : !online
      ? 'Update when online'
      : phase === 'failed'
        ? 'Retry update'
        : 'Update now';

  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed inset-x-0 top-0 z-[60] flex justify-center px-3 pt-[env(safe-area-inset-top,0px)]"
    >
      <div className="mt-2 flex w-full max-w-2xl items-center gap-3 rounded-xl border border-slate-300 bg-white px-4 py-2.5 shadow-md">
        <span className="text-sm text-slate-700">
          A new version is available — refresh to update.
          <span className="ml-1 text-slate-500 tabular-nums">
            {buildLabel(current)} &rarr; {buildLabel(latest)}
          </span>
        </span>
        <div className="ml-auto flex items-center gap-2">
          <button
            type="button"
            onClick={handleUpdate}
            disabled={applying || !online}
            title={!online && !applying ? 'Reconnect to update' : undefined}
            className={PRIMARY_BTN}
          >
            {applying && <span aria-hidden="true" className={SPINNER} />}
            {buttonText}
          </button>
          <button type="button" onClick={dismiss} className={GHOST_BTN}>
            Dismiss
          </button>
        </div>
      </div>
    </div>
  );
}
